import { Check, CircleDashed, Minus, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { api, errMsg } from "../../lib/api";
import { usePoll } from "../../hooks/usePoll";

const LOOK = {
  fixed: [Check, "text-emerald-300 border-emerald-500/40 bg-emerald-900/20", "Fixed"],
  partly: [Minus, "text-amber-300 border-amber-500/40 bg-amber-900/20", "Partly fixed"],
  not_fixed: [X, "text-rose-300 border-rose-500/40 bg-rose-900/20", "Not fixed"],
  not_rewritten: [X, "text-slate-400 border-slate-700", "Section not saved"],
  unknown: [CircleDashed, "text-slate-400 border-slate-700", "Couldn't check"],
  pending: [CircleDashed, "text-slate-400 border-slate-700", "Checking…"],
  waiting: [CircleDashed, "text-slate-400 border-slate-700", "Waiting for save"],
};

export const NoteTracker = ({ job, reload, noLeftovers }) => {
  const [, setParams] = useSearchParams();
  const running = job.notes_check === "running" || (job.status === "applied" && job.notes.some((n) => n.status === "pending"));
  usePoll(reload, running, 3000);
  const fixed = job.notes.filter((n) => n.status === "fixed").length;
  const leftover = job.status === "applied" && !running && !noLeftovers ? job.notes.filter((n) => ["not_fixed", "partly"].includes(n.status)).length : 0;
  const recheck = async () => { try { await api.post(`/rewrite-jobs/${job.id}/check-notes`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const sendBack = async () => {
    try {
      const { data } = await api.get(`/rewrite-jobs/${job.id}/leftover-handoff`);
      sessionStorage.setItem(`rewrite-prefill-${job.book_id}`, JSON.stringify(data));
      setParams({ tab: "rewrite" });
      window.dispatchEvent(new Event("rewrite-prefill"));
      toast.success(`${data.count} leftover note(s) carried to Rewrite — check and press start`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel p-5 space-y-3" data-testid="note-tracker">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><p className="eyebrow">Edit notes tracker</p>
          <p className="text-sm text-slate-300 mt-1" data-testid="note-tracker-summary">{job.status !== "applied" ? `${job.notes.length} note(s) will be checked once you save the rewrite.` : running ? "Checking each note against the rewrite…" : `${fixed} of ${job.notes.length} note(s) fixed`}</p></div>
        <div className="flex flex-wrap items-center gap-3">
          {leftover > 0 && <button type="button" onClick={sendBack} className="inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs bg-amber-600 hover:bg-amber-500 text-white transition-colors" data-testid="note-tracker-send-leftovers">Send {leftover} leftover note(s) to Rewrite →</button>}
          {job.status === "applied" && <button type="button" onClick={recheck} className="inline-flex items-center gap-1 text-xs text-amber-300 hover:underline" data-testid="note-tracker-recheck"><RefreshCw size={12} />Check again</button>}
        </div>
      </div>
      <ul className="space-y-2">
        {job.notes.map((n) => {
          const [Icon, tone, label] = LOOK[job.status === "applied" ? n.status : "waiting"] || LOOK.unknown;
          return (
            <li key={n.id} className="flex gap-3 items-start text-sm" data-testid={`note-${n.id}`}>
              <span className={`mt-0.5 shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${tone}`} data-testid={`note-status-${n.id}`}><Icon size={12} />{label}</span>
              <div className="min-w-0"><p className="text-[11px] font-mono text-slate-500">{n.chapter_title} · {n.category}</p><p className="text-slate-300">{n.text}</p></div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
